// src/lib/integrations.js
// Per-user Jira and Slack connections. Each SPCC user connects their own Jira
// site / Slack workspace with OAuth; the integrations API (Cloud Function `api`)
// holds the tokens. This module only sees connection status and the user's
// non-secret preferences (JQL, channel, …), which live in user_settings.
import { PROXY_URL } from './proxyAuth'
import { userSettings } from '../services/database'

// The API needs a Firebase sign-in, so integrations only work in Firebase mode.
export const integrationsEnabled = process.env.REACT_APP_DB_PROVIDER === 'firebase'

export const JIRA_DEFAULTS = { jql: 'updated >= -7d ORDER BY updated DESC', max: 200 }
export const SLACK_DEFAULTS = { channel: '', lookbackHours: 24 }
// The only fields kept in user_settings; anything else (e.g. legacy tokens) is dropped on save.
// jira.projects: keys of the Jira spaces (projects) to import from; empty = every space.
const PREFERENCE_KEYS = { jira: ['jql', 'max', 'projects'], slack: ['channel', 'lookbackHours', 'realtimeSync'] }

export async function integrationsRequest(path, { method, body, signal } = {}) {
  if (!integrationsEnabled) throw new Error('Hosted connections need Firebase sign-in. Local Ollama remains available in development.')
  const { getFirebaseUser } = await import('./firebase')
  const owner = await getFirebaseUser()
  if (!owner) throw new Error('Sign in to SPCC before connecting a source.')
  const token = await owner.getIdToken()
  const response = await fetch(`${PROXY_URL}${path}`, {
    method: method || (body ? 'POST' : 'GET'),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
    signal,
  })
  const data = await response.json().catch(() => ({}))
  if ((await getFirebaseUser())?.uid !== owner.uid) throw new Error('Your SPCC account changed. Open the connection again.')
  if (!response.ok) {
    const error = new Error(data.error || `Request failed (${response.status})`)
    error.status = response.status
    error.reconnect = !!data.reconnect
    throw error
  }
  return data
}

// { jira: { sites, site_id, connected_at } | null, slack: { team, connected_at } | null }
export const getIntegrationStatus = () => integrationsRequest('/integrations')
export const disconnectIntegration = (provider) => integrationsRequest(`/integrations/${provider}/disconnect`, { body: {} })
export const selectJiraSite = (siteId) => integrationsRequest('/integrations/jira/site', { body: { site_id: siteId } })
// Spaces (projects) on the selected Jira site, as [{ key, name }] sorted by name.
export const listJiraProjects = async () => {
  const { projects } = await integrationsRequest('/api/jira/projects', { body: {} })
  return (projects || []).map(({ key, name }) => ({ key, name })).sort((a, b) => a.name.localeCompare(b.name))
}

// The JQL actually sent to Jira: the user's filter, limited to the chosen spaces.
export function buildJiraJql({ jql, projects } = {}) {
  const query = (jql || JIRA_DEFAULTS.jql).trim()
  if (!projects?.length) return query
  const orderAt = query.search(/\border\s+by\b/i)
  const filter = (orderAt === -1 ? query : query.slice(0, orderAt)).trim()
  const orderBy = orderAt === -1 ? '' : ` ${query.slice(orderAt).trim()}`
  const inProjects = `project in (${projects.map(key => `"${key.replace(/["\\]/g, '\\$&')}"`).join(', ')})`
  return `${filter ? `${inProjects} AND (${filter})` : inProjects}${orderBy}`
}

export const listSlackChannels = async () => (await integrationsRequest('/slack/channels', { body: {} })).channels
export const postSlackMessage = (message) => integrationsRequest('/slack/webhook', { body: message })

// Opens the provider's consent screen in a popup. The API exchanges the code and
// stores the tokens, then the popup reports the outcome via postMessage.
export function connectIntegration(provider) {
  if (!['jira', 'slack'].includes(provider)) return Promise.reject(new Error('Unknown connection provider.'))
  // Open synchronously (inside the click) so the browser doesn't block the popup.
  const popup = window.open('', `spcc-${provider}-${Date.now()}`, 'width=600,height=720')
  if (!popup) return Promise.reject(new Error('The popup was blocked. Allow popups for this site and try again.'))
  const apiOrigin = new URL(PROXY_URL).origin

  return new Promise((resolve, reject) => {
    let poll, timer, settled = false
    const finish = (error) => {
      if (settled) return
      settled = true
      window.removeEventListener('message', onMessage)
      clearInterval(poll)
      clearTimeout(timer)
      error ? reject(error) : resolve()
    }
    const onMessage = (event) => {
      if (event.origin !== apiOrigin || event.source !== popup || event.data?.type !== 'SPCC_OAUTH_RESULT' || event.data.provider !== provider) return
      finish(event.data.ok ? null : new Error(event.data.error || 'Authorization failed.'))
    }
    window.addEventListener('message', onMessage)
    poll = setInterval(() => { if (popup.closed) finish(new Error('Authorization was cancelled.')) }, 800)
    timer = setTimeout(() => { popup.close(); finish(new Error('Sign-in timed out. Start the connection again.')) }, 15 * 60 * 1000)

    const start = provider === 'jira'
      ? integrationsRequest('/oauth/authorize')
      : integrationsRequest('/slack/authorize', { body: {} })
    start
      .then(({ authUrl }) => { popup.location.href = authUrl })
      .catch((error) => { popup.close(); finish(error) })
  })
}

// ── Preferences (non-secret) ────────────────────────────────────────────────

export async function getIntegrationSettings() {
  const settings = await userSettings.getSettings()
  return {
    jira: { ...JIRA_DEFAULTS, ...pick(settings.jira_config, PREFERENCE_KEYS.jira) },
    slack: { ...SLACK_DEFAULTS, ...pick(settings.slack_config, PREFERENCE_KEYS.slack) },
  }
}

export async function saveIntegrationSettings(provider, patch) {
  const key = `${provider}_config`
  const settings = await userSettings.getSettings()
  const next = { ...pick(settings[key], PREFERENCE_KEYS[provider]), ...pick(patch, PREFERENCE_KEYS[provider]) }
  for (const k of PREFERENCE_KEYS[provider]) if (k in patch && (patch[k] === undefined || patch[k] === '')) delete next[k]
  if (provider === 'slack') await integrationsRequest('/integrations/slack/channel', { body: { channel: next.realtimeSync ? next.channel : '' } })
  return userSettings.updateSettings({ [key]: next })
}

const pick = (obj, keys) => Object.fromEntries(keys.filter(k => obj?.[k] !== undefined && obj?.[k] !== '').map(k => [k, obj[k]]))

// Older versions kept API tokens and OAuth tokens in localStorage; remove them.
export function clearLegacyIntegrationStorage(userId) {
  for (const key of ['spcc:jira-config', 'spcc:slack-config', `spcc:user:${userId}:jira-config`, `spcc:user:${userId}:slack-config`]) {
    try { localStorage.removeItem(key) } catch {}
  }
}
